/**
 * CloseCompleteBlock — the intake Close/Reopen flow, the task
 * complete/reopen mirror, the task destructive actions (Stop repeating /
 * Delete task), and the 0155 intake destructive/lifecycle actions (Delete
 * intake — pre-setup dedup; Archive/Restore client — post-setup), extracted
 * from WorkItemDrawer (UX-11 PR-2; 0155 additions).
 *
 * Design pass 2026-07-25 (founder: "the bold underline close intake and
 * archive dont work"). Three changes, all inside the lifecycle cluster:
 *
 *   1. ONE action row. Close intake / Delete intake / Delete card /
 *      Archive-Restore client used to be four separately-margined blocks
 *      stacked down the drawer in three different treatments. They are now
 *      a single wrapping row (data-testid drawer-lifecycle-actions) sharing
 *      one baseline and one geometry, so the cluster reads as deliberate.
 *
 *   2. Matched treatment. "Close intake (didn't convert)" was a BOLD
 *      UNDERLINED text link — underline is navigation, and this is an
 *      action (standing law), so it is now a TextAction pill like every
 *      other inline action in the drawer. Archive client takes the
 *      DestructiveTextAction (pepper) register per the founder's ruling:
 *      identical shape, different tone. Closing an intake that didn't
 *      convert is a normal outcome, so it stays sage — if everything is
 *      red, red stops meaning anything.
 *
 *   3. Failure is visible. Both mutations fired into silence before: a
 *      rejected close or archive left the operator staring at an unchanged
 *      drawer with no error and no spinner, which is itself a way for an
 *      action to "not work". Both now show pending state and an alert line.
 *
 * DESIGN PASS, soak r2 (2026-07-25, punch items 1 and 5). The file now
 * exports TWO blocks instead of one, because the old single block mixed
 * two different kinds of thing and rendered both in the middle of the
 * scroll:
 *
 *   - LifecycleStatusBanner — the STATE ("Closed · no response",
 *     "Completed"). That is a header-level fact, so the drawer renders it
 *     at the TOP of the body, not two thirds of the way down.
 *   - LifecycleActions — everything you can DO to this record's life:
 *     Mark complete, Close intake, Delete, Archive/Restore. The drawer
 *     renders these in a PINNED FOOTER (punch item 1), so the exits are
 *     always reachable without scrolling and never interleave with the
 *     checklist, which is the work.
 *
 * The two Reopen affordances (punch item 5) were the last hand-rolled
 * bold-underlined buttons in this file: underline is navigation, and
 * these are actions. They are now TextActionOnBand — the pill geometry
 * every other drawer action wears, in the deeper on-band register the
 * pepper-soft / sage-100 washes need for AA. No hand-rolled button
 * chrome remains here, so this file LEAVES the raw-button allowlist (the
 * lint ratchets: a cleaned file drops its line in the same PR).
 *
 * The task delete/stop CONFIRM dialogs stay container-level (the dialog
 * cluster, migrating onto Dialog in PR-3); this block requests them via
 * onTaskAction. The 0155 intake-delete + archive/restore confirms instead
 * reuse the shared requestConfirm cluster (the SAME one the reversibility
 * block — move-back/unassign — already uses) so each gets its own copy
 * without a new dialog.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, type WorkItemDetail } from '@/lib/api';
import { useTenant } from '@/lib/auth';
import type { useCloseIntake, useDeleteIntake, useDeleteProcessItem, usePatchWorkItem, useReopenIntake, useCompleteRecurringTask } from '@/lib/workspace';
import type { useSetClientArchived } from '@/lib/clientsApi';
import { ASSIGNED_STAGE_KEY } from '@/lib/workspaceStageMove';
import { Button } from '@/components/primitives/Button';
import { TextAction, TextActionOnBand, DestructiveTextAction } from '@/components/workspace/TextAction';
import { ACTION_INSET } from './drawerLayout';
import type { ConfirmActionRequest } from './AssignReversibilityBlock';
import { SelectField } from '@/components/primitives/SelectField';
import { contactSentence, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

/**
 * A failed close, in words the operator can act on. The server refuses a
 * blank disposition (founder ruling 2026-07-25); an older tab that still
 * allows one must not report that as a mystery failure.
 */
function closeErrorMessage(err: unknown, contact: SupportContact): string {
  if (err instanceof ApiError && (err.body as { error?: string } | null)?.error === 'close_reason_required') {
    return 'Pick a reason before closing.';
  }
  return contactSentence("The intake didn't close. Try again, and if it happens again, tell {contact_system}.", contact);
}

export interface LifecycleStatusBannerProps {
  detail: WorkItemDetail;
  canManage: boolean;
  reopenIntake: ReturnType<typeof useReopenIntake>;
  patchItem: ReturnType<typeof usePatchWorkItem>;
}

/**
 * The lifecycle STATE band — closed (intake) or completed (one-off task).
 * Rendered at the top of the drawer body: "this record is closed" is the
 * first thing that changes how you read everything below it. aria-live
 * announces the transition; the pepper-soft / sage-100 bands plus the dot
 * carry the signal, so the label itself clears AA contrast.
 */
export function LifecycleStatusBanner({
  detail,
  canManage,
  reopenIntake,
  patchItem,
}: LifecycleStatusBannerProps): ReactNode {
  const showTaskDone = detail.kind === 'task' && !detail.recurrence_id && detail.status === 'done';
  return (
    <div aria-live="polite">
      {detail.closed_at ? (
        <div className="flex items-center gap-2 rounded-card bg-pepper-soft px-3 py-2" data-testid="drawer-closed-banner">
          <span className="w-1.5 h-1.5 rounded-pill bg-pepper flex-shrink-0" aria-hidden="true" />
          <span className="text-[13px] text-sage-800 font-semibold">Closed{detail.close_reason ? ` · ${detail.close_reason}` : ''}</span>
          {canManage && (
            <TextActionOnBand data-testid="drawer-reopen" className="ml-auto" onClick={() => reopenIntake.mutate(detail.id)}>
              Reopen
            </TextActionOnBand>
          )}
        </div>
      ) : showTaskDone ? (
        <div className="flex items-center gap-2 rounded-card bg-sage-100 px-3 py-2" data-testid="drawer-task-done-banner">
          <span className="w-1.5 h-1.5 rounded-pill bg-sage-600 flex-shrink-0" aria-hidden="true" />
          <span className="text-[13px] text-sage-800 font-semibold">Completed</span>
          {canManage && (
            <TextActionOnBand data-testid="drawer-task-reopen" className="ml-auto" onClick={() => patchItem.mutate({ id: detail.id, edits: { status: 'todo' } })}>
              Reopen
            </TextActionOnBand>
          )}
        </div>
      ) : null}
    </div>
  );
}

export interface LifecycleActionsProps {
  detail: WorkItemDetail;
  canManage: boolean;
  /** LP-2: whether Delete task / Stop repeating show. The office always;
   *  an own-tasks holder only on a task they made. Defaults to canManage. */
  canRemoveTask?: boolean;
  /** The resolved key of detail.stage_id (the drawer resolves it against the
   *  card's process stages) — needed for the arm-3 converted test below. */
  currentStageKey: string | null;
  closeReasons: readonly string[];
  closeIntake: ReturnType<typeof useCloseIntake>;
  patchItem: ReturnType<typeof usePatchWorkItem>;
  completeRecurring: ReturnType<typeof useCompleteRecurringTask>;
  onTaskAction: (action: 'delete' | 'stop') => void;
  /** 0155 — the shared styled-confirm request cluster (AssignReversibilityBlock's). */
  requestConfirm: (req: ConfirmActionRequest) => void;
  /** 0155 Part A — hard-delete an UNCONVERTED intake. */
  deleteIntake: ReturnType<typeof useDeleteIntake>;
  /** 0160 — hard-delete a process-board card (candidate cleanup). */
  deleteProcessItem: ReturnType<typeof useDeleteProcessItem>;
  /** Fires after a successful intake/process-card delete (the drawer closes — there's nothing left to show). */
  onIntakeDeleted: () => void;
  /** 0155 Part B — gates the Archive/Restore client affordance (manage_client_identity). */
  canManageClientIdentity: boolean;
  /** null = unknown/loading/not-applicable; true = the linked client is archived (offer Restore);
   *  false = active (offer Archive). Only meaningful when detail.client_id is set. */
  clientArchived: boolean | null;
  /** 0155 Part B — the EXISTING client soft-archive mutation (reused, not rebuilt). */
  archiveClient: ReturnType<typeof useSetClientArchived>;
}

/**
 * Everything you can DO to this record's life. Lives in the drawer's
 * pinned footer, so the exits are one place, always reachable, and never
 * interleaved with the checklist.
 */
export function LifecycleActions({
  detail,
  canManage,
  canRemoveTask,
  currentStageKey,
  closeReasons,
  closeIntake,
  patchItem,
  completeRecurring,
  onTaskAction,
  requestConfirm,
  deleteIntake,
  deleteProcessItem,
  onIntakeDeleted,
  canManageClientIdentity,
  clientArchived,
  archiveClient,
}: LifecycleActionsProps): ReactNode {
  const contact = useSupportContact();
  // 0155 — the FULL three-arm "converted" test (conversionSemantics.ts /
  // conversionMetrics; the SAME predicate delete_intake's SQL guard uses):
  //   (1) client_id minted (reached Client Setup), OR
  //   (2) close_reason='completed', OR
  //   (3) OPEN (closed_at null) AND parked in the terminal Assigned stage —
  //       the backfill shape whose client_id lands only at cutover.
  // Delete is a PRE-setup dedup tool; a converted intake is NEVER deletable.
  const convertedByStage = detail.closed_at === null && currentStageKey === ASSIGNED_STAGE_KEY;
  const isConvertedIntake = detail.client_id !== null || detail.close_reason === 'completed' || convertedByStage;
  const canDeleteIntake = canManage && detail.kind === 'intake' && !isConvertedIntake;
  // Archive/Restore reuses the CLIENT soft-archive, so it needs a linked client
  // row. A converted-by-stage backfill intake has NO client_id yet → there is
  // nothing to archive; that case shows the informational note below instead.
  const canArchiveClientHere = canManageClientIdentity && detail.kind === 'intake' && detail.client_id !== null;
  // Converted but with no client record to archive AND not deletable (arm 3, or a
  // completed close whose client_id hasn't landed) — explain why neither action shows.
  const showConvertedNoClientNote =
    canManage && detail.kind === 'intake' && isConvertedIntake && detail.client_id === null;
  // Converted WITH a client record: Delete is never offered (delete_intake
  // refuses a converted intake by design), so say why in plain words and
  // point at the client record, where Archive lives. Read-only viewers never
  // had the control, so they get no explanation of it.
  const showConvertedClientNote = canManage && detail.kind === 'intake' && detail.client_id !== null;
  const { tenantSlug } = useTenant();
  const [closing, setClosing] = useState(false);
  const [closeReason, setCloseReason] = useState('');

  // The lifecycle row's membership. While the close form is open the row is
  // suppressed: a red "Delete intake" sitting directly under an open
  // "Close intake" form is two destructive-looking exits at once, and the
  // operator is mid-flow on one of them.
  const showCloseTrigger = canManage && detail.kind === 'intake' && !detail.closed_at;
  const showTaskDestructive = (canRemoveTask ?? canManage) && detail.kind === 'task';
  const showProcessDelete = canManage && detail.kind === 'process_item';
  const showArchiveAction = canArchiveClientHere && clientArchived !== null;
  const showLifecycleRow =
    !closing && (showCloseTrigger || canDeleteIntake || showProcessDelete || showArchiveAction || showTaskDestructive);
  // The task's completion control (the one primary action a task drawer has).
  // A RECURRING task must NOT patch status to 'done' — the backend refuses
  // that (workItems.ts: "a recurring task never goes 'done' via PATCH"); it
  // advances IN PLACE via completeRecurring (this occurrence recorded done,
  // the same card rolls to its next due date and stays 'todo'). Wiring both
  // to the status patch is why marking a recurring task complete silently
  // did nothing. A recurring task therefore shows no persistent "Completed"
  // banner — it just advances.
  const showTaskComplete = canManage && detail.kind === 'task' && (detail.recurrence_id ? true : detail.status !== 'done');

  // Nothing to offer (a reader, or a closed task) — the footer renders
  // nothing rather than an empty bar.
  if (!showLifecycleRow && !showTaskComplete && !closing && !showConvertedNoClientNote && !showConvertedClientNote) return null;

  return (
    <div className="border-t border-sage-200 bg-paper px-5 py-3">
      {showTaskComplete && (
        detail.recurrence_id ? (
          <Button className="mb-2 w-full" data-testid="drawer-task-complete-recurring"
            loading={completeRecurring.isPending}
            onClick={() => completeRecurring.mutate(detail.id)}>
            Mark complete
          </Button>
        ) : (
          <Button className="mb-2 w-full" data-testid="drawer-task-complete" onClick={() => patchItem.mutate({ id: detail.id, edits: { status: 'done' } })}>
            Mark complete
          </Button>
        )
      )}

      {showCloseTrigger && closing && (
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Founder ruling 2026-07-25: no blank close. The picker starts
                unselected and the confirm stays disabled until a reason is
                chosen — including the explicit "no reason recorded" option,
                which the server always serves in this list, so "we don't
                know" is a thing you can SAY rather than a blank you leave. */}
            <SelectField size="compact" value={closeReason} onChange={(e) => setCloseReason(e.target.value)} data-testid="close-reason-select" aria-label="Close reason"
              >
              <option value="">Select a reason</option>
              {closeReasons.map((r) => <option key={r} value={r}>{r}</option>)}
            </SelectField>
            {/* loading, so a slow close can't be double-fired into two POSTs. */}
            <Button size="sm" data-testid="close-confirm" loading={closeIntake.isPending}
              disabled={closeReason.trim() === ''}
              onClick={() => closeIntake.mutate({ id: detail.id, reason: closeReason }, { onSuccess: () => setClosing(false) })}>
              Close intake
            </Button>
            <TextAction onClick={() => setClosing(false)}>Cancel</TextAction>
          </div>
          {closeReason.trim() === '' && (
            <p className="mt-1.5 text-[11.5px] text-ink-soft" data-testid="drawer-close-reason-hint">
              Pick a reason to close. If you don’t know, there’s an option for that.
            </p>
          )}
          {closeIntake.isError && (
            <p role="alert" data-testid="drawer-close-error" className="mt-2 text-xs text-pepper-deep">
              {closeErrorMessage(closeIntake.error, contact)}
            </p>
          )}
        </div>
      )}

      {/* THE LIFECYCLE ACTION ROW — one baseline, one geometry, ordered
          least-consequential first (leave → remove). ACTION_INSET cancels
          the first pill's own padding so the row's text optically aligns
          with the drawer's shared left edge. */}
      {showLifecycleRow && (
        <div className={`flex flex-wrap items-center gap-x-1 gap-y-1 ${ACTION_INSET}`} data-testid="drawer-lifecycle-actions">
          {/* Not destructive: an intake that didn't convert is a normal
              outcome, and it is reversible via Reopen. Sage register. */}
          {showCloseTrigger && (
            <TextAction data-testid="drawer-close-intake" onClick={() => setClosing(true)}>
              Close intake (didn’t convert)
            </TextAction>
          )}

          {/* Destructive: hard-delete a one-off task. A recurring task gets
              "Stop repeating" (deactivate the rule) so its series is not
              orphaned — the function refuses to delete a recurring instance. */}
          {showTaskDestructive && (
            detail.recurrence_id ? (
              <TextAction data-testid="drawer-task-stop-repeating" onClick={() => onTaskAction('stop')}>Stop repeating</TextAction>
            ) : (
              <DestructiveTextAction data-testid="drawer-task-delete" onClick={() => onTaskAction('delete')}>
                Delete task
              </DestructiveTextAction>
            )
          )}

          {/* 0155 Part A — Delete intake (pre-client-setup DEDUP). Shown ONLY for an
              UNCONVERTED intake (no client record yet, not completed): deleting a
              converted intake would destroy its client linkage + skew conversion
              analytics, so a converted intake is ARCHIVED (Part B) instead. The
              confirm names the person + warns it removes them from the tracker
              AND the conversion numbers. */}
          {canDeleteIntake && (
            <DestructiveTextAction
              data-testid="drawer-intake-delete"
              onClick={() => requestConfirm({
                title: 'Delete this intake?',
                body: `This permanently deletes ${detail.display_name || detail.prospect_name || detail.title || 'this intake'} and removes it from your tracker and your conversion numbers. It can’t be undone. Use this for duplicates or mistaken entries, not to close a real intake that didn’t convert.`,
                confirmLabel: 'Delete intake',
                destructive: true,
                run: () => deleteIntake.mutate(detail.id, { onSuccess: onIntakeDeleted }),
              })}
            >
              Delete intake
            </DestructiveTextAction>
          )}

          {/* 0160 — Delete a process-board card (candidate cleanup on hiring-style
              boards: duplicates, withdrawn applicants, test cards). No converted
              guard — a process card is board WIP, never a client linkage or a
              conversion-funnel record. */}
          {showProcessDelete && (
            <DestructiveTextAction
              data-testid="drawer-process-item-delete"
              onClick={() => requestConfirm({
                title: 'Delete this card?',
                body: `This permanently deletes ${detail.display_name || detail.title || 'this card'} and its checklist and activity. It can’t be undone.`,
                confirmLabel: 'Delete card',
                destructive: true,
                run: () => deleteProcessItem.mutate(detail.id, { onSuccess: onIntakeDeleted }),
              })}
            >
              Delete card
            </DestructiveTextAction>
          )}

          {/* 0155 Part B — Archive / Restore the CONVERTED intake's client (the
              EXISTING client soft-archive, reused). Shown ONLY when the intake has a
              linked client record, gated manage_client_identity. clientArchived is
              null while the roster lookup loads → render nothing until we know which
              direction to offer (never flash the wrong verb). Archiving drops the
              client from the active roster; the intake card can stay in Completed.

              Archive carries the pepper register (founder ruling 2026-07-25) and a
              pepper confirm to match, even though it is reversible — dropping
              someone off the active roster is consequential enough to earn the
              pause. Restore is the undo, so it stays sage. */}
          {showArchiveAction && (
            clientArchived ? (
              <TextAction
                data-testid="drawer-client-restore"
                disabled={archiveClient.isPending}
                onClick={() => requestConfirm({
                  title: 'Restore this client?',
                  confirmLabel: 'Restore client',
                  run: () => archiveClient.mutate({ id: detail.client_id!, archived: false }),
                })}
              >
                Restore client
              </TextAction>
            ) : (
              <DestructiveTextAction
                data-testid="drawer-client-archive"
                disabled={archiveClient.isPending}
                onClick={() => requestConfirm({
                  title: 'Archive this client?',
                  body: 'Archiving hides the client from the default roster. Nothing is deleted: the record, notes, and history stay, and the client can be restored any time.',
                  confirmLabel: 'Archive client',
                  destructive: true,
                  run: () => archiveClient.mutate({ id: detail.client_id!, archived: true }),
                })}
              >
                Archive client
              </DestructiveTextAction>
            )
          )}
        </div>
      )}

      {/* A rejected archive/restore used to fail into total silence: the confirm
          dialog closed and nothing else happened. */}
      {showArchiveAction && archiveClient.isError && (
        <p role="alert" data-testid="drawer-archive-error" className="mt-2 text-xs text-pepper-deep">
          {contactSentence("The client wasn't updated. Try again, and if it happens again, tell {contact_system}.", contact)}
        </p>
      )}

      {/* 0155 — a CONVERTED intake with NO client record yet (the open-at-Assigned
          backfill shape, or a completed close whose client link hasn't landed):
          it can't be deleted (it's a won conversion) and there's no client to
          archive. Explain, rather than offering a dead button. */}
      {showConvertedNoClientNote && (
        <p className="mt-2 text-xs text-sage-400" data-testid="drawer-converted-no-client-note">
          Converted. The client record will link once the CareTree migration is complete.
        </p>
      )}
      {showConvertedClientNote && (
        <p className="mt-2 text-xs text-sage-400" data-testid="drawer-converted-client-note">
          This intake became a client. Archive the client from the roster instead.{' '}
          <Link
            to={`/t/${tenantSlug}/clients/${detail.client_id}`}
            className="underline decoration-dotted underline-offset-2 text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
          >
            Open the client record
          </Link>
        </p>
      )}
    </div>
  );
}
